import React, { useState, useEffect } from 'react';
import { withFormik } from 'formik';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
// components
import Modal from 'shared/modals/Modal';
import { FormikInput, FormikTextarea, FormikLabel } from 'shared/forms';
import toastr from 'shared/helpers/toastr';
import errorToastr from 'shared/helpers/errorToastr';
import LoadingModal from 'shared/loading/LoadingModal';
import LocationPicker from 'shared/pickers/LocationPicker';
// fetch
import { getBobtail, postBobtail } from 'src/fetch/dispatching';
import { getDistanceBetweenAddresses } from 'src/fetch/orders';
// selectors
import { getLocations } from 'src/slices/locations';

const BobtailModal = ({
  isShowing,
  onClose,
  initialValues,
  setFieldValue,
  setFieldTouched,
  values,
  handleChange,
  handleBlur,
  handleSubmit,
  touched,
  errors,
  isSubmitting,
  setSubmitting,
  resetForm,
  legData,
  readOnly = false
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const locations = useSelector(getLocations);

  const closeModal = () => {
    resetForm();
    onClose(false);
  };

  // google miles calculations and maps
  const [isLoadingMiles, setIsLoadingMiles] = useState(false);
  const calculateMiles = async (bobtailLocation, type) => {
    const stop = locations.find(x => x.id === bobtailLocation);
    let milesResponse;
    if (stop) {
      try {
        setIsLoadingMiles(true);

        if (type === 'origin') {
          milesResponse = await getDistanceBetweenAddresses(
            `${legData.origin.locationAddress}, ${legData.origin.locationCityStateZipString}`,
            `${stop.address1}, ${stop.address2 ? `${stop.address2},` : ''}${stop.cityStateZipString}`
          );
          setFieldValue('origin.miles', milesResponse);
        } else {
          milesResponse = await getDistanceBetweenAddresses(
            `${stop.address1}, ${stop.address2 ? `${stop.address2},` : ''}${stop.cityStateZipString}`,
            `${legData.destination.locationAddress}, ${legData.destination.locationCityStateZipString}`,
          );
          setFieldValue('destination.miles', milesResponse);
        }

        setIsLoadingMiles(false);
      } catch (error) {
        errorToastr({
          error,
          messagePrefix: 'Could not calculate miles between locations.'
        });
        setIsLoadingMiles(false);
      }
    }
  };

  // when locations change or on initial load if we don't have miles, calculate
  useEffect(() => {
    if (values.origin && values.origin.locationId && !values.origin.miles) {
      calculateMiles(values.origin.locationId, 'origin');
    }
    if (values.destination && values.destination.locationId && !values.destination.miles) {
      calculateMiles(values.destination.locationId, 'destination');
    }
  }, [values.origin.locationId, values.destination.locationId]);

  return (
    <Modal isOpen={isShowing} handleClose={closeModal} containerStyle='large'>
      <div className='modal-header'>
        <h5 className='modal-title' id='bobtail-modal'>{readOnly ? 'View' : 'Add'} Bobtail</h5>
        <button className='button large close' onClick={closeModal} />
      </div>
      <section className={`bobtail-form ${isSubmitting ? 'hide-overflow' : ''}`}>
        <LoadingModal isOpen={isSubmitting || isLoadingMiles} isNested={true} />
        <form onSubmit={handleSubmit}>
          <section className='modal-body formik-form' style={{ display: 'flex', flexDirection: 'column' }}>
            <div className='formik-row'>
              <div style={{ width: '50%', marginRight: '1rem' }}>
                <label>Bobtail To Origin</label>
                <LocationPicker
                  disabled={readOnly}
                  onChange={location => {
                    setFieldValue('origin.locationId', location.id);
                    calculateMiles(location.id, 'origin');
                  }}
                  value={values.origin && values.origin.locationId}
                  initialLocations={locations && locations.filter(x => !x.deleted)}
                />
              </div>
              <FormikLabel
                label='Bobtail Miles'
                name='origin.miles'
                value={values.origin.miles}
                wrapperClass='wrapper-four-column'
              />
              <div style={{ width: '25%' }}>
                {!readOnly && (
                  <button
                    type='button'
                    className='button large primary text-align--center toolbar--button--margin'
                    disabled={!values.origin.locationId}
                    onClick={() => {
                      setFieldValue('origin', { locationId: null, miles: 0 });
                    }}
                  >
                    Remove Bobtail
                  </button>
                )}
              </div>
            </div>
            <div className='formik-row'>
              <FormikLabel
                label='Leg Origin'
                value={legData.origin.locationName}
                wrapperClass='wrapper-one-column'
              />
            </div>
            <div className='formik-row'>
              <FormikLabel
                label='Leg Destination'
                value={legData.destination.locationName}
                wrapperClass='wrapper-one-column'
              />
            </div>
            <div className='formik-row'>
              <div style={{ width: '50%', marginRight: '1rem' }}>
                <label>Bobtail From Destination</label>
                <LocationPicker
                  disabled={readOnly}
                  onChange={location => {
                    setFieldValue('destination.locationId', location.id);
                    calculateMiles(location.id, 'destination');
                  }}
                  value={values.destination && values.destination.locationId}
                  initialLocations={locations && locations.filter(x => !x.deleted)}
                />
              </div>
              <FormikLabel
                label='Bobtail Miles'
                name='destination.miles'
                value={values.destination.miles}
                wrapperClass='wrapper-four-column'
              />
              <div style={{ width: '25%' }}>
                {!readOnly && (
                  <button
                    type='button'
                    className='button large primary text-align--center toolbar--button--margin'
                    disabled={!values.destination.locationId}
                    onClick={() => {
                      setFieldValue('destination', { locationId: null, miles: 0 });
                    }}
                  >
                    Remove Bobtail
                  </button>
                )}
              </div>
            </div>
            <div className='formik-row'>
              <FormikTextarea
                wrapperClass='wrapper-one-column'
                type='text'
                label='Comments'
                name='comments'
                value={values.comments}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                disabled={readOnly}
              />
            </div>
          </section>
          <section className='modal-footer toolbar toolbar--xxs'>
            <div className='toolbar--column toolbar--right'>
              {!readOnly && (
                <button
                  type='submit'
                  className='button large secondary text-align--center toolbar--button--margin'
                  disabled={isSubmitting || isLoadingMiles}
                >
                  Save
                </button>
              )}
              <button
                type='reset'
                id='bobtail-cancel'
                className='button large white text-align--center margin--none--xxs'
                onClick={() => closeModal()}
              >
                {readOnly ? 'Close' : 'Cancel'}
              </button>
            </div>
          </section>
        </form>
      </section>
    </Modal>
  );
};

const BobtailSchema = Yup.object().shape({
  orderLegId: Yup.string().required(),
  comments: Yup.string().nullable(),
});

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ legData = {} }) => {
    return {
      orderLegId: legData ? legData.legId : '',
      origin: {
        locationId: legData.bobtail.origin ? legData.bobtail.origin.locationId : null,
        miles: legData.bobtail.origin ? legData.bobtail.origin.miles : 0,
      },
      destination: {
        locationId: legData.bobtail.destination ? legData.bobtail.destination.locationId : null,
        miles: legData.bobtail.destination ? legData.bobtail.destination.miles : 0,
      },
      comments: legData ? legData.bobtail.comments : '',
    };
  },
  validationSchema: BobtailSchema,
  handleSubmit: async (values, { setSubmitting, resetForm, props: { onClose } }) => {
    setSubmitting(true);

    const data = {
      bobtail: { ...values }
    };

    try {
      await postBobtail(data);
      onClose(true);
      resetForm();

      toastr('success', 'Bobtail Saved Successfully.');
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'Bobtail Save Failed!'
      });
      setSubmitting(false);
    }
  }
})(BobtailModal);
